import { Injectable } from '@angular/core';
import { createStore, emitOnce, filterNil } from '@ngneat/elf';
import {
  deleteAllEntities,
  deleteEntities,
  entitiesPropsFactory,
  getEntity,
  selectEntity,
  updateEntities,
  upsertEntities,
  upsertEntitiesById,
} from '@ngneat/elf-entities';
import { distinctUntilChanged, filter, map, merge, switchMap } from 'rxjs';

import { isEqual } from 'lodash';
import { UserInfo } from '../models/user';
import { SpaceRepository } from './space.repository';

export interface SelectedItemData {
  item: fabric.Object;
  origin: SelectedItemOrigin;
}

export interface CommentsData {
  itemId: string;
  commentContent: string;
}

export interface SelectedCommentData {
  commentId: string | undefined;
  origin: SelectionOrigin;
}

export enum SelectionOrigin {
  CANVAS = 'canvas',
  PANEL = 'panel',
}

export enum ApiResponseStatus {
  FAILED = 'failed',
  SUCCEEDED = 'succeeded',
}

export interface CurrentApiCallStatus {
  itemId: string;
  commentId: string;
  status: ApiResponseStatus;
}

export interface SelectedItemData {
  item: fabric.Object;
  origin: SelectedItemOrigin;
}

export enum SelectedItemOrigin {
  CONTEXT_MENU = 'context_menu',
  CANVAS = 'canvas',
}

export interface MovingCommentedItem {
  item: fabric.Object | undefined;
  moving: boolean;
}

export interface ItemChangeData {
  changedItemsIds: Set<string>;
  newPosition?: Map<string, Position>;
  action: ItemAction;
  local: boolean;
}

export interface Position {
  top: number;
  left: number;
  width: number;
  height: number;
}

export enum ItemAction {
  RESTORED = 'restored',
  REMOVED = 'removed',
  POSITION_CHANGED = 'position_changed',
}

export interface UICommentData {
  _id: string;
  relatedItemId: string;
  content: any[];
  author: UserInfo;
  moving: boolean;
  deleted: boolean;
  edited: boolean;
  updatedAt: string;
}

export interface IBoardUICommentsData {
  boardUid: string;
  // maps comment id to UI comments data
  uiCommentsData: Record<string, UICommentData>;
}

export interface IBoardCommentedItemsData {
  boardUid: string;
  // maps items id to IBoardCommentedItemData
  commentedItems: Record<string, IBoardCommentedItemData>;
}

export interface ISpaceCommentState {
  boardUid: string;
  selectedComment: SelectedCommentData | undefined;
  hoveredCommentId: string | undefined;
  selectedItemData: SelectedItemData;
  movingCommentedItem: MovingCommentedItem;
  loadingNewComment: boolean;
  switchingToBoard: string | undefined;
  deletingAllCommentsStatus: ApiResponseStatus;
  deletingCommentStatus: {
    itemId: string;
    commentId: string;
    status: ApiResponseStatus;
  };
  creatingCommentCancelled: boolean;
}

export interface IBoardCommentedItemData {
  itemId: string;
  top: number;
  left: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  transform: string;
}

const { CommentStateEntitiesRef, withCommentStateEntities } = entitiesPropsFactory('CommentState');
const { CommentedItemsEntitiesRef, withCommentedItemsEntities } =
  entitiesPropsFactory('CommentedItems');
const { BoardUICommentDataEntitiesRef, withBoardUICommentDataEntities } =
  entitiesPropsFactory('BoardUICommentData');

@Injectable({
  providedIn: 'root',
})
export class SpaceCommentsRepository {
  constructor(private spaceRepo: SpaceRepository) {}
  private readonly store = createStore(
    { name: 'spaceComments' },
    withBoardUICommentDataEntities<IBoardUICommentsData, 'boardUid'>({ idKey: 'boardUid' }),
    withCommentedItemsEntities<IBoardCommentedItemsData, 'boardUid'>({ idKey: 'boardUid' }),
    withCommentStateEntities<ISpaceCommentState, 'boardUid'>({ idKey: 'boardUid' }),
  );

  upsertUIComments(uiCommentsData: UICommentData[]): void {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    uiCommentsData.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    this.store.update(
      upsertEntities(
        {
          boardUid: boardUid,
          uiCommentsData: this.buildRecords(uiCommentsData, '_id'),
        },
        {
          ref: BoardUICommentDataEntitiesRef,
        },
      ),
    );
  }

  upsertCurrentBoardItems(commentedItems: IBoardCommentedItemData[]): void {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return;
    }

    const addedCommentedItemsDataRecords = this.buildRecords(commentedItems, 'itemId');
    emitOnce(() => {
      this.store.update(
        upsertEntitiesById(boardUid, {
          updater: (entity) => ({
            boardUid: boardUid,
            commentedItems: { ...entity.commentedItems, ...addedCommentedItemsDataRecords },
          }),
          creator: (_id) => ({
            boardUid: boardUid,
            commentedItems: addedCommentedItemsDataRecords,
          }),
          ref: CommentedItemsEntitiesRef,
        }),
      );
    });
  }

  public deleteBoardCommentAndRelatedItem(id: string, boardUid?: string) {
    if (!boardUid) {
      boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    }
    const currentUIComments = this.getStoreUICommentsRecords(boardUid);
    const currentCommentedItems = this.getStoreCommentedItemsRecords(boardUid);
    const commentToDelete = currentUIComments[id];
    if (!commentToDelete) {
      return;
    }

    delete currentUIComments[id];
    delete currentCommentedItems[commentToDelete.relatedItemId];

    emitOnce(() => {
      // delete comment
      this.store.update(
        updateEntities(
          boardUid!,
          { boardUid: boardUid, uiCommentsData: currentUIComments },
          { ref: BoardUICommentDataEntitiesRef },
        ),
      );

      // delete related item
      this.store.update(
        updateEntities(
          boardUid!,
          {
            boardUid: boardUid,
            commentedItems: currentCommentedItems,
          },
          { ref: CommentedItemsEntitiesRef },
        ),
      );
    });
  }

  public deleteBoardCommentsAndRelatedItems(boardUid?: string) {
    const currentBoardUid = boardUid ?? this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!currentBoardUid) {
      return;
    }
    emitOnce(() => {
      this.store.update(deleteEntities(currentBoardUid, { ref: CommentedItemsEntitiesRef }));
      this.store.update(deleteEntities(currentBoardUid, { ref: BoardUICommentDataEntitiesRef }));
    });
  }

  public resetRepo() {
    emitOnce(() => {
      this.store.update(deleteAllEntities({ ref: BoardUICommentDataEntitiesRef }));
      this.store.update(deleteAllEntities({ ref: CommentedItemsEntitiesRef }));
      this.store.update(deleteAllEntities({ ref: CommentStateEntitiesRef }));
    });
  }

  public upsertUIBoardComment(id: string, updatedComment: UICommentData) {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return;
    }
    const updatedCommentRecord = { [updatedComment._id]: updatedComment };
    emitOnce(() => {
      this.store.update(
        upsertEntitiesById(boardUid, {
          ref: BoardUICommentDataEntitiesRef,
          updater: (entity) => ({
            boardUid: boardUid,
            uiCommentsData: { ...entity.uiCommentsData, ...updatedCommentRecord },
          }),
          creator: (_id) => ({ boardUid: boardUid, uiCommentsData: updatedCommentRecord }),
        }),
      );
    });
  }

  public updateUIBoardCommentByitemId(itemId: string, updatedComment: Partial<UICommentData>) {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return;
    }
    const uiCommentData = this.getUICommentByItemId(boardUid, itemId);
    if (!uiCommentData) {
      return;
    }

    const uiCommentsDataRecords = this.getStoreUICommentsRecords(boardUid);
    uiCommentsDataRecords[uiCommentData._id] = {
      ...uiCommentData,
      ...updatedComment,
    };

    emitOnce(() => {
      this.store.update(
        updateEntities(
          boardUid,
          { boardUid: boardUid, uiCommentsData: uiCommentsDataRecords },
          { ref: BoardUICommentDataEntitiesRef },
        ),
      );
    });
  }

  public updateUIBoardComment(commentId: string, updatedComment: Partial<UICommentData>) {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return;
    }

    const uiCommentsDataRecords = this.getStoreUICommentsRecords(boardUid);
    if (!uiCommentsDataRecords) {
      return;
    }

    uiCommentsDataRecords[commentId] = { ...uiCommentsDataRecords[commentId], ...updatedComment };
    emitOnce(() => {
      this.store.update(
        updateEntities(
          boardUid,
          { boardUid: boardUid, uiCommentsData: uiCommentsDataRecords },
          { ref: BoardUICommentDataEntitiesRef },
        ),
      );
    });
  }

  public updateCommentsState(updatedCommentState: Partial<ISpaceCommentState>): void {
    const boardUid = this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return;
    }
    emitOnce(() => {
      this.store.update(
        updateEntities(boardUid, updatedCommentState, {
          ref: CommentStateEntitiesRef,
        }),
      );
    });
  }

  /** fires as soon as user picks a new board
   * fires before fabric/canvas items are populated on board
   * and also before spaceRepo's activeBoardUid$ fires
   */

  public readonly switchingToBoard$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        map((c) => c?.switchingToBoard),
        filter((c) => c !== undefined),
      ),
    ),
  );

  public readonly UIBoardComments$ = merge(
    this.spaceRepo.activeSpaceSelectedBoardUid$
      .pipe(
        filterNil(),
        distinctUntilChanged(),
        switchMap((boardUid) =>
          this.store.pipe(selectEntity(boardUid, { ref: BoardUICommentDataEntitiesRef })),
        ),
      )
      .pipe(
        filterNil(),
        map((commentsMap) => Object.values(commentsMap.uiCommentsData) || []),
        map((x) =>
          x.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
        ),
      ),
    this.switchingToBoard$.pipe(map(() => [] as UICommentData[])),
  );

  public readonly commentedItems$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store
        .pipe(selectEntity(boardUid, { ref: CommentedItemsEntitiesRef }))
        .pipe(map((boardItemsData) => Object.values(boardItemsData?.commentedItems || {}))),
    ),
  );

  public readonly selectedComment$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        filterNil(),
        map((o) => o?.selectedComment),
        distinctUntilChanged(),
      ),
    ),
  );

  public readonly hoveredCommentId$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        filterNil(),
        map((o) => o?.hoveredCommentId),
        distinctUntilChanged(),
      ),
    ),
  );

  public readonly selectedItemData$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        filterNil(),
        map((o) => o?.selectedItemData),
        distinctUntilChanged(),
      ),
    ),
  );

  public readonly creatingCommentCancelled$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store
        .pipe(
          selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
          map((c) => c?.creatingCommentCancelled),
        )
        .pipe(filter((cancelled) => cancelled === true)),
    ),
  );

  public readonly movingCommentedItem$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        map((c) => c?.movingCommentedItem),
      ),
    ),
  );

  public readonly deletingAllCommentsStatus$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        map((c) => c?.deletingAllCommentsStatus),
        distinctUntilChanged(isEqual),
      ),
    ),
  );

  public readonly deletingCommentStatus$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        map((c) => c?.deletingCommentStatus),
        distinctUntilChanged(isEqual),
      ),
    ),
  );

  public readonly loadingNewComment$ = this.spaceRepo.activeSpaceSelectedBoardUid$.pipe(
    filterNil(),
    switchMap((boardUid) =>
      this.store.pipe(
        selectEntity(boardUid, { ref: CommentStateEntitiesRef }),
        map((c) => c?.loadingNewComment),
        filterNil(),
      ),
    ),
  );

  getUICommentByItemId(boardUid: string, id: string) {
    return this.getStoreUIComments(boardUid).find((c) => c.relatedItemId === id);
  }

  getStoreUIComments(boardUid?: string): UICommentData[] {
    return Object.values(this.getStoreUICommentsRecords(boardUid));
  }

  getStoreUICommentsRecords(boardUid?: string): Record<string, UICommentData> {
    boardUid = boardUid ?? this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return {};
    }
    return (
      this.store.query(getEntity(boardUid, { ref: BoardUICommentDataEntitiesRef }))
        ?.uiCommentsData || {}
    );
  }

  getStoreCommentedItems(boardUid?: string): IBoardCommentedItemData[] {
    return Object.values(this.getStoreCommentedItemsRecords());
  }

  getStoreCommentedItemsRecords(boardUid?: string): Record<string, IBoardCommentedItemData> {
    boardUid = boardUid ?? this.spaceRepo.activeSpaceSelectedBoardUid;
    if (!boardUid) {
      return {};
    }
    return (
      this.store.query(getEntity(boardUid, { ref: CommentedItemsEntitiesRef }))?.commentedItems ||
      {}
    );
  }

  private buildRecords<T>(array: T[], key: string) {
    return array.reduce((dataRecords, element) => {
      dataRecords[element[key]] = element;
      return dataRecords;
    }, {});
  }
}
